import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import type { Pki } from "~/modules/pki/models/Pki.interface";
import type { PkiCertificateAuthority } from "~/modules/pki/models/PkiCertificateAuthority.interface";
import type { PkiConnectivity } from "~/modules/pki/models/PkiConnectivity.interface";
import type { PkiConnectivityPayload } from "~/modules/pki/models/PkiConnectivityPayload.interface";
import { PkiConnectivityState } from "~/modules/pki/models/PkiConnectivityState.enum";

export class PkiService extends RestService {
  protected PKI_CONNECTOR = "pki-connector";
  protected CONFIGURATIONS = "configurations";
  protected CERTIFICATE_AUTHORITIES = "certificate-authorities";
  protected CONNECTIVITY = "connectivity";

  async getPkis({
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<PaginatedList<Pki>> {
    const params = { page, pageSize, sort, filter: searchParams };
    return this.$apiV2.get(this.getConfigurationsPath(), {
      params,
    });
  }

  async getPki(alias: string): Promise<Pki> {
    return this.$apiV2.get(this.getConfigurationPath(alias));
  }

  async deletePki(alias: string): Promise<void> {
    return this.$apiV2.delete(this.getConfigurationPath(alias));
  }

  async createPki(pki: Pki): Promise<void> {
    return this.$apiV2.post(this.getConfigurationsPath(), pki);
  }

  async getCertificateAuthorities(): Promise<PkiCertificateAuthority[]> {
    return this.$apiV2.get(
      `${this.PKI_CONNECTOR}/${this.CERTIFICATE_AUTHORITIES}`,
    );
  }

  async updatePki(alias: string, pki: Pki): Promise<void> {
    return this.$apiV2.patch(this.getConfigurationPath(alias), pki);
  }

  async getConnectivity(pkiAlias: string): Promise<boolean> {
    const response: PkiConnectivity = await this.$apiV2.get(
      `${this.getConfigurationPath(pkiAlias)}/${this.CONNECTIVITY}`,
    );
    return this.mapResponseConnectivity(response);
  }

  async getConnectivityWithInfos(
    pki: PkiConnectivityPayload,
  ): Promise<boolean> {
    const response: PkiConnectivity = await this.$apiV2.post(
      `${this.getConfigurationsPath()}/${this.CONNECTIVITY}`,
      pki,
    );
    return this.mapResponseConnectivity(response);
  }

  private getConfigurationsPath() {
    return `${this.PKI_CONNECTOR}/${this.CONFIGURATIONS}`;
  }

  private getConfigurationPath(alias: string) {
    return `${this.PKI_CONNECTOR}/${this.CONFIGURATIONS}/${alias}`;
  }

  private mapResponseConnectivity(response: PkiConnectivity) {
    return (
      response?.connectivity === PkiConnectivityState.OK &&
      response?.authentication === PkiConnectivityState.OK
    );
  }
}
export default new PkiService();
